<script lang="ts">
  import { store } from './lib/store.svelte.ts';
  import { router } from './lib/router.svelte.ts';
  import Round from './routes/Round.svelte';
  import Carts from './routes/Carts.svelte';
  import CartDetail from './routes/CartDetail.svelte';
  import Sites from './routes/Sites.svelte';
  import SiteDetail from './routes/SiteDetail.svelte';
  import Labels from './routes/Labels.svelte';
  import Reports from './routes/Reports.svelte';
  import RoundDetail from './routes/RoundDetail.svelte';
  import Settings from './routes/Settings.svelte';
  import Login from './routes/Login.svelte';
  import Icon from './lib/Icon.svelte';

  const tabs = [
    { key: 'round', label: 'Contrôle', icon: 'scan', to: '' },
    { key: 'carts', label: 'Chariots', icon: 'cart', to: 'carts' },
    { key: 'sites', label: 'Sites', icon: 'warehouse', to: 'sites' },
    { key: 'reports', label: 'Rapports', icon: 'chart', to: 'reports' },
    { key: 'settings', label: 'Réglages', icon: 'settings', to: 'settings' },
  ] as const;

  /** Detail screens light up the tab they belong to, so the rail never looks unrelated. */
  const TAB_OF: Record<string, string> = {
    cart: 'carts',
    site: 'sites',
    labels: 'sites',
    'round-detail': 'reports',
  };
  const activeTab = $derived(TAB_OF[router.current.name] ?? router.current.name);

  const syncLabel = $derived(
    store.syncing
      ? 'Synchronisation…'
      : !store.connected
        ? store.pending > 0
          ? `${store.pending} à envoyer`
          : 'Hors connexion'
        : store.pending > 0
          ? `${store.pending} en attente`
          : 'À jour',
  );
  const syncTone = $derived(
    !store.connected ? 'warn' : store.pending > 0 ? 'accent' : store.lastSyncError ? 'danger' : 'ok',
  );
</script>

{#if !store.booted}
  <div class="page"><p class="muted"><span class="spinner"></span> Chargement…</p></div>
{:else if !store.identity}
  <!-- No identity, no application. The gate is here rather than per route so a new screen
       cannot accidentally be reachable without one. -->
  <Login />
{:else}
  <div class="app-shell">
    <nav class="navbar no-print" aria-label="Navigation principale">
      {#each tabs as t (t.key)}
        <button
          aria-current={activeTab === t.key ? 'page' : undefined}
          onclick={() => router.go(t.to)}
        >
          <Icon name={t.icon} size={19} />
          <span>{t.label}</span>
        </button>
      {/each}
    </nav>

    <div class="main-col">
      <header class="topbar no-print">
        <span class="title brand-on-mobile">Chariot</span>
        <span class="spacer"></span>
        <button class="sync-btn" onclick={() => store.sync()} title="Forcer une synchronisation">
          {#if store.syncing}
            <span class="spinner" aria-hidden="true"></span>
          {:else}
            <span class="dot {syncTone}" aria-hidden="true"></span>
          {/if}
          <span>{syncLabel}</span>
        </button>
      </header>

      <main
        class="page"
        class:column={['settings', 'cart', 'site', 'round-detail'].includes(router.current.name)}
      >
        {#if !store.connected}
          <!-- Reassurance, not an alarm: this is the condition the app is designed for, and
               the operator's work is already safe on the device. -->
          <div class="banner warn">
            Hors connexion — {store.pending > 0
              ? `vos ${store.pending} scan${store.pending === 1 ? '' : 's'} ${store.pending === 1 ? 'est' : 'sont'} gardé${store.pending === 1 ? '' : 's'} sur cet appareil et partiront`
              : 'tout est déjà enregistré ici et repartira'} automatiquement au retour du réseau.
          </div>
        {:else if store.lastSyncError}
          <div class="banner danger">{store.lastSyncError}</div>
        {/if}

        {#if router.current.name === 'round'}
          <Round />
        {:else if router.current.name === 'carts'}
          <Carts />
        {:else if router.current.name === 'cart'}
          <CartDetail code={router.current.code} />
        {:else if router.current.name === 'sites'}
          <Sites />
        {:else if router.current.name === 'site'}
          <SiteDetail code={router.current.code} />
        {:else if router.current.name === 'labels'}
          <Labels />
        {:else if router.current.name === 'reports'}
          <Reports />
        {:else if router.current.name === 'round-detail'}
          <RoundDetail id={router.current.id} />
        {:else}
          <Settings />
        {/if}
      </main>
    </div>
  </div>
{/if}

<style>
  /* The navy rail already carries the product name on wide screens. */
  @media (min-width: 860px) {
    .brand-on-mobile { display: none; }
  }
  .sync-btn {
    min-height: var(--control-h-sm);
    padding: 0 .6rem;
    gap: .4rem;
    font-size: var(--text-meta);
    font-weight: 500;
    color: var(--fg-muted);
    background: var(--surface);
  }
  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex: none;
    background: var(--fg-subtle);
  }
  .dot.ok { background: var(--success); }
  .dot.accent { background: var(--primary); }
  .dot.warn { background: var(--warning); }
  .dot.danger { background: var(--danger); }
</style>
